/**
 * Numeração SNCR (Anvisa) para receitas controladas.
 *
 * O login gov.br acontece numa página-ponte em domínio .br (exigência da
 * Anvisa). Aqui só criamos o pedido, abrimos a ponte num popup e acompanhamos
 * o resultado pelo backend.
 */
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export type SncrReceitaTipo = 'RCE' | 'RET' | 'NRA' | 'NRB' | 'NRB2' | 'NRR' | 'NRT';

export const SNCR_TIPOS: SncrReceitaTipo[] = ['RCE', 'RET', 'NRA', 'NRB', 'NRB2', 'NRR', 'NRT'];
export const SNCR_NOTIFICATION_TIPOS: SncrReceitaTipo[] = ['NRA', 'NRB', 'NRB2', 'NRR', 'NRT'];

export const SNCR_TIPO_LABELS: Record<SncrReceitaTipo, string> = {
  RCE: 'Receita de Controle Especial',
  RET: 'Receita Especial de Retenção',
  NRA: 'Notificação de Receita A',
  NRB: 'Notificação de Receita B',
  NRB2: 'Notificação de Receita B2',
  NRR: 'Notificação de Retinoides',
  NRT: 'Notificação de Talidomida',
};

export const SNCR_TIPO_HINTS: Record<SncrReceitaTipo, string> = {
  RCE: 'Listas C1, C4 e C5 · 1.000 números por pedido (até 3 pedidos/mês)',
  RET: 'Antimicrobianos · 1.000 números por pedido (até 3 pedidos/mês)',
  NRA: 'Listas A1, A2, A3 · exige saldo autorizado pela VISA',
  NRB: 'Lista B1 · exige saldo autorizado pela VISA',
  NRB2: 'Lista B2 · exige saldo autorizado pela VISA',
  NRR: 'Lista C2 · exige saldo autorizado pela VISA',
  NRT: 'Lista C3 · exige saldo autorizado pela VISA',
};

export function isSncrNotificationTipo(tipo: string): boolean {
  return SNCR_NOTIFICATION_TIPOS.includes(tipo as SncrReceitaTipo);
}

export interface SncrPublicConfig {
  enabled: boolean;
  environment: 'hmg' | 'prd';
  bridge_url: string;
  enforce: boolean;
  platform_name: string;
  platform_cnpj_configured: boolean;
}

export interface SncrBalance {
  receita_tipo: SncrReceitaTipo;
  available: number;
  issued_this_month: number;
  requests_this_month: number;
  last_block: { created_at: string; quantidade: number; saldo_sncr: number | null; inicio: string | null; fim: string | null } | null;
}

export interface SncrOverview extends SncrPublicConfig {
  professional: {
    id: string;
    name: string;
    conselho: string | null;
    documento: string | null;
    uf: string | null;
    issue: string | null;
  };
  balances: SncrBalance[];
}

export interface SncrPlanItem {
  id: string | null;
  label: string;
  /** C1, B1… · ATM (antimicrobiano) · CTRL (controlado sem classe). */
  control_class: string | null;
}

export interface SncrPlanDocument {
  slot: string;
  receita_tipo: SncrReceitaTipo;
  month_index: number;
  item_ids: string[];
  items?: SncrPlanItem[];
  reserved: boolean;
  numero?: string | null;
  /** Preenchidos no plano de lote (uma receita por data programada). */
  prescription_id?: string | null;
  prescription_number?: string | null;
  issue_date?: string | null;
}

export interface SncrPrescriptionPlan extends SncrPublicConfig {
  professional_id: string | null;
  documents: SncrPlanDocument[];
  needs: Array<{ receita_tipo: SncrReceitaTipo; needed: number; available: number; missing: number }>;
}

export interface SncrRequestedItem {
  receita_tipo: SncrReceitaTipo;
  quantidade?: number;
}

export interface SncrRequestResult {
  receita_tipo: SncrReceitaTipo;
  ok: boolean;
  quantidade?: number;
  error?: string | null;
}

export interface SncrAuthRequestStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'partial' | 'failed' | 'expired';
  error_message: string | null;
  result: SncrRequestResult[] | null;
  expires_at: string;
}

function apiError(error: any, fallback: string): Error {
  return new Error(error?.response?.data?.error || error?.message || fallback);
}

export async function getSncrConfig(): Promise<SncrPublicConfig> {
  try {
    const response = await axios.get(getApiUrl('/clinic/sncr/config'));
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível carregar a configuração SNCR.');
  }
}

export async function getSncrOverview(professionalId: string): Promise<SncrOverview> {
  try {
    const response = await axios.get(getApiUrl(`/clinic/sncr/professionals/${professionalId}/overview`));
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível carregar a numeração SNCR do profissional.');
  }
}

export async function getPrescriptionSncrPlan(prescriptionId: string): Promise<SncrPrescriptionPlan> {
  try {
    const response = await axios.get(getApiUrl(`/clinic/sncr/prescriptions/${prescriptionId}/plan`));
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível verificar a numeração SNCR da receita.');
  }
}

/** Plano somado de várias receitas do mesmo prescritor (assinatura em lote). */
export async function getPrescriptionsSncrPlanBatch(prescriptionIds: string[]): Promise<SncrPrescriptionPlan> {
  try {
    const response = await axios.post(getApiUrl('/clinic/sncr/prescriptions/plan-batch'), {
      prescription_ids: prescriptionIds,
    });
    return response.data?.data;
  } catch (error) {
    throw apiError(error, 'Não foi possível verificar a numeração SNCR do lote.');
  }
}

/**
 * Reserva a numeração do lote inteiro antes de o token A3 assinar uma a uma.
 * Falta de saldo volta com `sncrCode`, como na assinatura.
 */
export async function reserveSncrBatch(prescriptionIds: string[]): Promise<void> {
  try {
    await axios.post(getApiUrl('/clinic/sncr/prescriptions/reserve-batch'), {
      prescription_ids: prescriptionIds,
    });
  } catch (error: any) {
    const out = apiError(error, 'Não foi possível reservar a numeração SNCR do lote.') as Error & {
      sncrCode?: string;
      sncrDetails?: any;
    };
    const code = error?.response?.data?.code;
    if (typeof code === 'string' && code.startsWith('SNCR_')) {
      out.sncrCode = code;
      out.sncrDetails = error?.response?.data?.details;
    }
    throw out;
  }
}

async function createAuthRequest(professionalId: string, requested: SncrRequestedItem[]) {
  try {
    const response = await axios.post(getApiUrl('/clinic/sncr/auth-requests'), {
      professional_id: professionalId,
      requested,
    });
    return response.data?.data as {
      id: string;
      state: string;
      environment: 'hmg' | 'prd';
      expires_at: string;
      bridge_url: string;
    };
  } catch (error) {
    throw apiError(error, 'Não foi possível iniciar o pedido de numeração.');
  }
}

export async function getSncrAuthRequest(id: string): Promise<SncrAuthRequestStatus> {
  const response = await axios.get(getApiUrl(`/clinic/sncr/auth-requests/${id}`));
  return response.data?.data;
}

/** Base absoluta da API deste tenant (a ponte roda em outro domínio). */
function absoluteApiBase(): string {
  const marker = '/public/sncr/complete';
  const full = new URL(getApiUrl(marker), window.location.href).toString();
  return full.slice(0, full.length - marker.length);
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const FINAL_STATUSES = new Set(['completed', 'partial', 'failed', 'expired']);

export class SncrFlowCancelled extends Error {
  constructor() {
    super('Pedido de numeração cancelado.');
    this.name = 'SncrFlowCancelled';
  }
}

export interface SncrGovBrRequest {
  id: string;
  /** Link da ponte gov.br — serve no computador (popup) ou no celular (QR). */
  url: string;
  expires_at: string;
  environment: 'hmg' | 'prd';
}

export const SNCR_GOVBR_POPUP_NAME = 'klinvia-sncr-govbr';

/** Cria o pedido e monta o link da ponte; o login pode ser feito em qualquer aparelho. */
export async function startSncrGovBrRequest(
  professionalId: string,
  requested: SncrRequestedItem[],
): Promise<SncrGovBrRequest> {
  const request = await createAuthRequest(professionalId, requested);
  const url = new URL(request.bridge_url);
  url.searchParams.set('st', request.state);
  url.searchParams.set('env', request.environment);
  url.searchParams.set('api', absoluteApiBase());
  url.searchParams.set('o', window.location.origin);
  return { id: request.id, url: url.toString(), expires_at: request.expires_at, environment: request.environment };
}

/** Acompanha o pedido no backend até a numeração ser registrada (ou falhar/expirar). */
export async function waitForSncrAuthRequest(params: {
  requestId: string;
  expiresAt: string;
  onStatus?: (text: string) => void;
  signal?: AbortSignal;
}): Promise<SncrAuthRequestStatus> {
  const deadline = new Date(params.expiresAt).getTime();
  // `popup.closed` não serve de cancelamento: o COOP do gov.br corta a referência
  // e o navegador passa a reportar a janela como fechada no meio do login.
  for (;;) {
    await sleep(2000);
    if (params.signal?.aborted) throw new SncrFlowCancelled();
    const status = await getSncrAuthRequest(params.requestId);
    if (FINAL_STATUSES.has(status.status)) return status;
    if (status.status === 'processing') params.onStatus?.('Gerando numeração no SNCR…');
    if (Date.now() > deadline) throw new Error('O pedido de numeração expirou. Tente de novo.');
  }
}

/**
 * Abre a ponte gov.br e espera o backend registrar a numeração. Chame direto
 * do clique: o popup precisa nascer no mesmo gesto do usuário.
 */
export async function runSncrGovBrFlow(params: {
  professionalId: string;
  requested: SncrRequestedItem[];
  onStatus?: (text: string) => void;
  signal?: AbortSignal;
}): Promise<SncrAuthRequestStatus> {
  const popup = window.open('', SNCR_GOVBR_POPUP_NAME, 'width=520,height=720');
  try {
    const request = await startSncrGovBrRequest(params.professionalId, params.requested);
    if (popup && !popup.closed) {
      popup.location.href = request.url;
    } else {
      const fallback = new URL(request.url);
      fallback.searchParams.set('ret', window.location.href);
      window.location.href = fallback.toString();
      return await new Promise<never>(() => undefined);
    }
    params.onStatus?.('Conclua o login gov.br na janela aberta.');
    return await waitForSncrAuthRequest({
      requestId: request.id,
      expiresAt: request.expires_at,
      onStatus: params.onStatus,
      signal: params.signal,
    });
  } catch (error) {
    if (popup && !popup.closed) popup.close();
    throw error;
  }
}
